import {
  Box,
  Flex,
  ScreenReaderOnly,
  SkeletonBox,
} from '@moonpig/launchpad-components'
import { spacing } from '@moonpig/launchpad-theme'
import { styled, useTheme } from '@moonpig/launchpad-utils'
import React from 'react'
import { useLocaleText } from './SkeletonCarousel.locale'

const CARD_WIDTH = '200px'
const CARD_HEIGHT = '300px'

const StyledCarousel = styled(Flex)`
  overflow: hidden;
  margin: auto;
`

type SkeletonCarouselProps = {
  /** The number of boxes to load */
  numberOfBoxes?: number
  /** Whether or not to hide the skeleton 'title' box */
  hideTitle?: boolean
  /** Alter the height of the boxes within the carousel */
  boxHeight?: number | string
  /** Alter the width of the boxes within the carousel */
  boxWidth?: number | string
  /** Spacing between boxes in carousel - uses [launchpad scale system](https://docs.launchpad.moonpig.io/?path=/docs/foundations-spacing--page) */
  boxSpacing?: number
  /** Text which can be used by a screen reader to help identify the component */
  screenReaderText?: string
}

const titleWidth = spacing(16)
const titleHeight = spacing(10)

export const SkeletonCarousel: React.FC<
  React.PropsWithChildren<SkeletonCarouselProps>
> = ({
  numberOfBoxes = 6,
  hideTitle = false,
  boxHeight = CARD_HEIGHT,
  boxWidth = CARD_WIDTH,
  boxSpacing = 6,
  screenReaderText,
}: SkeletonCarouselProps) => {
  const titleAnimationDelay = 0
  const cardAnimationDelayMultiplier = 0.5
  const theme = useTheme()
  const localiseText = useLocaleText()

  return (
    <Box mb={10} data-testid="skeleton-carousel">
      <ScreenReaderOnly>{screenReaderText}</ScreenReaderOnly>
      {!hideTitle && (
        <SkeletonBox
          width={titleWidth({ theme })}
          height={titleHeight({ theme })}
          borderRadius={1}
          mb={6}
          animationDelay={titleAnimationDelay}
          screenReaderText={
            screenReaderText ||
            localiseText('skeleton_carousel.loading_indicator_carousel')
          }
        />
      )}
      <StyledCarousel>
        {[...Array(numberOfBoxes)].map((_, index) => (
          <SkeletonBox
            height={boxHeight}
            minWidth={boxWidth}
            mr={boxSpacing}
            borderRadius={1}
            animationDelay={index * cardAnimationDelayMultiplier}
            // eslint-disable-next-line react/no-array-index-key
            key={`skeleton-box-${index}`}
            screenReaderText={
              screenReaderText ||
              localiseText(
                'skeleton_carousel.loading_indicator_carousel_product_tile',
              )
            }
            data-testid="card"
          />
        ))}
      </StyledCarousel>
    </Box>
  )
}
